import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  ApiConsumer,
  ApiConsumerId,
  ApiConsumerJwt,
  ApiConsumerName,
  ConnectedUserJwt,
  WithConventionId,
} from "shared";
import type { PayloadActionWithFeedbackTopic } from "src/core-logic/domain/feedback/feedback.slice";

type ApiConsumerState = {
  isLoading: boolean;
  apiConsumers: ApiConsumer[];
  lastCreatedToken: ApiConsumerJwt | null;
  apiConsumerNames: ApiConsumerName[];
};

export const apiConsumerInitialState: ApiConsumerState = {
  isLoading: false,
  apiConsumers: [],
  lastCreatedToken: null,
  apiConsumerNames: [],
};

export const apiConsumerSlice = createSlice({
  name: "apiConsumer",
  initialState: apiConsumerInitialState,
  reducers: {
    fetchApiConsumerNamesRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<
        WithConventionId & { jwt: ConnectedUserJwt }
      >,
    ) => {
      state.isLoading = true;
    },

    fetchApiConsumerNamesSucceeded: (
      state,
      {
        payload,
      }: PayloadActionWithFeedbackTopic<{
        apiConsumerNames: ApiConsumerName[];
      }>,
    ) => {
      state.isLoading = false;
      state.apiConsumerNames = payload.apiConsumerNames;
    },

    fetchApiConsumerNamesFailed: (
      state,
      _action: PayloadActionWithFeedbackTopic<{ errorMessage: string }>,
    ) => {
      state.isLoading = false;
    },
    clearFetchedApiConsumerNames: (state) => {
      state.apiConsumerNames = apiConsumerInitialState.apiConsumerNames;
    },
    retrieveApiConsumersRequested: (
      state,
      _action: PayloadAction<ConnectedUserJwt>,
    ) => {
      state.isLoading = true;
    },
    retrieveApiConsumersSucceeded: (
      state,
      action: PayloadAction<ApiConsumer[]>,
    ) => {
      state.isLoading = false;
      state.apiConsumers = action.payload;
    },
    retrieveApiConsumersFailed: (state) => {
      state.isLoading = false;
    },
    saveApiConsumerRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        apiConsumer: ApiConsumer;
        adminToken: ConnectedUserJwt;
      }>,
    ) => {
      state.isLoading = true;
    },
    saveApiConsumerSucceeded: (
      state,
      action: PayloadActionWithFeedbackTopic<{
        apiConsumerJwt: ApiConsumerJwt;
      }>,
    ) => {
      state.isLoading = false;
      state.lastCreatedToken = action.payload.apiConsumerJwt;
    },
    updateApiConsumerSucceeded: (
      state,
      _action: PayloadActionWithFeedbackTopic,
    ) => {
      state.isLoading = false;
    },
    saveApiConsumerFailed: (
      state,
      _action: PayloadActionWithFeedbackTopic<{ errorMessage: string }>,
    ) => {
      state.isLoading = false;
    },
    clearLastCreatedToken: (state) => {
      state.lastCreatedToken = null;
    },
    clearApiConsumersRequested: (state) => {
      state.isLoading = false;
      state.apiConsumers = [];
    },
    revokeApiConsumerRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        apiConsumerId: ApiConsumerId;
        adminToken: ConnectedUserJwt;
      }>,
    ) => {
      state.isLoading = true;
    },
    revokeApiConsumerSucceeded: (
      state,
      _action: PayloadActionWithFeedbackTopic,
    ) => {
      state.isLoading = false;
    },
    revokeApiConsumerFailed: (
      state,
      _action: PayloadActionWithFeedbackTopic<{ errorMessage: string }>,
    ) => {
      state.isLoading = false;
    },
    renewApiConsumerKeyRequested: (
      state,
      _action: PayloadActionWithFeedbackTopic<{
        apiConsumerId: ApiConsumerId;
        adminToken: ConnectedUserJwt;
      }>,
    ) => {
      state.isLoading = true;
    },
    renewApiConsumerKeySucceeded: (
      state,
      action: PayloadActionWithFeedbackTopic<{
        apiConsumerJwt: ApiConsumerJwt;
      }>,
    ) => {
      state.isLoading = false;
      state.lastCreatedToken = action.payload.apiConsumerJwt;
    },
    renewApiConsumerKeyFailed: (
      state,
      _action: PayloadActionWithFeedbackTopic<{ errorMessage: string }>,
    ) => {
      state.isLoading = false;
    },
  },
});
